import { isColumnCompatibleWithTag } from "./SourceTypeColumns";
import {
  coerceToBoolean,
  lookupIdToName,
  matchesSourceType,
  parseHwioChannelNumber,
  parseJsonObject,
} from "./Helpers";

const modbusFields = {
  ModbusRegister: {
    getValue: (deviceParams) => deviceParams?.regnum,
    setValue: (deviceParams, value) => ({ ...deviceParams, regnum: value }),
  },
  ModbusIP: {
    getValue: (deviceParams) => deviceParams?.connection?.ip,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      connection: { ...deviceParams?.connection, ip: value },
    }),
  },
  ModbusPort: {
    getValue: (deviceParams) => deviceParams?.connection?.port,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      connection: { ...deviceParams?.connection, port: value },
    }),
  },
  ModbusPortRTU: {
    getValue: (deviceParams) => deviceParams?.connection?.port_number,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      connection: { ...deviceParams?.connection, port_number: value },
    }),
  },
  ModbusSlaveId: {
    getValue: (deviceParams) => deviceParams?.connection?.slave_id,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      connection: { ...deviceParams?.connection, slave_id: value },
    }),
  },
  LoSetpoint: {
    getValue: (deviceParams) =>
      deviceParams?.properties_to_sync?.properties?.LoLimit?.regnum,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      properties_to_sync: {
        ...deviceParams?.properties_to_sync,
        properties: {
          ...deviceParams?.properties_to_sync?.properties,
          LoLimit: {
            ...deviceParams?.properties_to_sync?.properties?.LoLimit,
            regnum: value,
          },
        },
      },
    }),
  },
  LoLoSetpoint: {
    getValue: (deviceParams) =>
      deviceParams?.properties_to_sync?.properties?.LoLoLimit?.regnum,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      properties_to_sync: {
        ...deviceParams?.properties_to_sync,
        properties: {
          ...deviceParams?.properties_to_sync?.properties,
          LoLoLimit: {
            ...deviceParams?.properties_to_sync?.properties?.LoLoLimit,
            regnum: value,
          },
        },
      },
    }),
  },
  HiSetpoint: {
    getValue: (deviceParams) =>
      deviceParams?.properties_to_sync?.properties?.HiLimit?.regnum,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      properties_to_sync: {
        ...deviceParams?.properties_to_sync,
        properties: {
          ...deviceParams?.properties_to_sync?.properties,
          HiLimit: {
            ...deviceParams?.properties_to_sync?.properties?.HiLimit,
            regnum: value,
          },
        },
      },
    }),
  },
  HiHiSetpoint: {
    getValue: (deviceParams) =>
      deviceParams?.properties_to_sync?.properties?.HiHiLimit?.regnum,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      properties_to_sync: {
        ...deviceParams?.properties_to_sync,
        properties: {
          ...deviceParams?.properties_to_sync?.properties,
          HiHiLimit: {
            ...deviceParams?.properties_to_sync?.properties?.HiHiLimit,
            regnum: value,
          },
        },
      },
    }),
  },
  SyncDirection: {
    getValue: (deviceParams) =>
      deviceParams?.properties_to_sync?.sync_direction,
    setValue: (deviceParams, value) => ({
      ...deviceParams,
      properties_to_sync: {
        ...deviceParams?.properties_to_sync,
        sync_direction: value,
      },
    }),
  },
};

const lookupFieldMappings = {
  TagSourceTypeId: { stringField: "SourceType", category: "TagSourceType" },
  AlarmTypeId: { stringField: "AlarmType", category: "TagAlarmType" },
  DataTypeId: { stringField: "DataType", category: "TagDataType" },
  AlarmLimitRangeId: {
    stringField: "AlarmLimitRange",
    category: "AlarmLimitRange",
  },
  AlarmSeverityId: {
    stringField: "AlarmSeverity",
    category: "AlarmSeverity",
  },
  AlarmArmingTypeId: {
    stringField: "AlarmArmingType",
    category: "AlarmArmingType",
  },
};

// HardWiredIO fields live directly on the tag record rather than in DeviceParams. Each entry
// converts the bulk value into the form the underlying field stores.
const hwioFields = {
  TagTypeId: { setValue: (value) => parseInt(value) },
  // Channel numbers are entered 1-based and may carry an I/O type prefix ("AI 5") to match the
  // grid and the shared tag editor; the prefix is ignored and the value is stored 0-based.
  ChannelNumber: { setValue: (value) => parseHwioChannelNumber(value) - 1 },
  PortNumber: { setValue: (value) => parseInt(value) },
};

/**
 * Whether a field applies to a tag's source type. Restricted fields name the grid column whose
 * rule they share, so the source types themselves live only in SourceTypeColumns.js.
 */
const isTagCompatibleWithField = (tag, fieldInfo, lookupValues) => {
  if (fieldInfo?.columnId) {
    return isColumnCompatibleWithTag(fieldInfo.columnId, tag, lookupValues);
  }
  if (fieldInfo?.sourceTypes?.length) {
    return matchesSourceType(tag, fieldInfo.sourceTypes, lookupValues);
  }
  return true;
};

/**
 * Sets one field to the same value on every selected tag that the field applies to. Tags the
 * field does not apply to (`skippedCount`), or that cannot take the value (`invalidValueCount`),
 * are skipped: never written and never marked dirty. Returns new grid state and leaves the inputs
 * untouched.
 * @param {object} update
 * @param {object[]} update.editedTags Rows as shown in the grid.
 * @param {Set<number>} update.dirtyTags Indexes of rows that differ from their saved baseline.
 * @param {Iterable<number>} update.indices Indexes of the rows to update.
 * @param {object} update.fieldInfo The bulk update field: `key`, plus optional `name`, `type`,
 *   `columnId` and `sourceTypes`.
 * @param {*} update.value The value entered for the field.
 * @param {object[]} update.lookupValues Lookup value categories from LookupValueContext.
 * @returns {{ editedTags: object[], dirtyTags: Set<number>, updatedCount: number,
 *   skippedCount: number, invalidValueCount: number }}
 */
export const applyBulkUpdate = ({
  editedTags,
  dirtyTags,
  indices,
  fieldInfo,
  value: newValue,
  lookupValues,
}) => {
  const fieldName = fieldInfo?.key;
  let newEditedTags = [...editedTags];
  let newDirtyTags = new Set(dirtyTags);
  let updatedCount = 0;
  let skippedCount = 0;
  let invalidValueCount = 0;

  for (const index of indices) {
    const tag = newEditedTags[index];

    // Guard before writing anything: incompatible tags are skipped entirely, never written and
    // never marked dirty.
    if (!isTagCompatibleWithField(tag, fieldInfo, lookupValues)) {
      skippedCount++;
      continue;
    }

    if (hwioFields[fieldName]) {
      const storedValue = hwioFields[fieldName].setValue(newValue);
      if (Number.isNaN(storedValue)) {
        console.warn(
          `Skipping tag ${index} - "${newValue}" is not a valid ${fieldName}`
        );
        invalidValueCount++;
        continue;
      }

      newEditedTags[index] = { ...tag, [fieldName]: storedValue };
      newDirtyTags.add(index);
      updatedCount++;
      continue;
    }

    if (modbusFields[fieldName]) {
      const modbusField = modbusFields[fieldName];

      try {
        let deviceParams = {};
        if (tag.DeviceParams) {
          try {
            deviceParams = JSON.parse(tag.DeviceParams);
          } catch (e) {
            console.warn(
              `Invalid DeviceParams for tag ${index}, creating new structure`
            );
            deviceParams = { connection: {} };
          }
        } else {
          deviceParams = { connection: {} };
        }

        const updatedDeviceParams = modbusField.setValue(
          deviceParams,
          newValue
        );

        newEditedTags[index] = {
          ...tag,
          DeviceParams: JSON.stringify(updatedDeviceParams),
        };
      } catch (error) {
        console.error(`Error updating DeviceParams for tag ${index}:`, error);
        invalidValueCount++;
        continue;
      }
    } else {
      if (fieldName === "Hidden") {
        const dynamicProperties = parseJsonObject(tag.DynamicProperties, {});
        dynamicProperties.isHidden = coerceToBoolean(newValue);
        const updatedTag = {
          ...tag,
          DynamicProperties: JSON.stringify(dynamicProperties),
        };
        delete updatedTag.Hidden;
        newEditedTags[index] = updatedTag;
        newDirtyTags.add(index);
        updatedCount++;
        continue;
      }

      if (fieldName === "SafetyShutdown") {
        const dynamicProperties = parseJsonObject(tag.DynamicProperties, {});
        const flags = dynamicProperties.flags || {};
        const isEnabled = coerceToBoolean(newValue);

        if (isEnabled) {
          flags["Safety Shutdown"] = true;
        } else {
          delete flags["Safety Shutdown"];
        }

        if (Object.keys(flags).length > 0) {
          dynamicProperties.flags = flags;
        } else {
          delete dynamicProperties.flags;
        }

        const updatedTag = {
          ...tag,
          DynamicProperties: JSON.stringify(dynamicProperties),
        };
        delete updatedTag.SafetyShutdown;
        newEditedTags[index] = updatedTag;
        newDirtyTags.add(index);
        updatedCount++;
        continue;
      }

      const derivesFromTagIdentifier =
        (fieldName === "AlarmId" && newValue === "__TAG_IDENTIFIER__") ||
        (fieldName === "Alarm2Id" && newValue === "5+__TAG_IDENTIFIER__");
      if (
        derivesFromTagIdentifier &&
        Number.isNaN(parseInt(tag.TagIdentifier))
      ) {
        console.warn(
          `Skipping tag ${index} - it has no Tag ID to derive ${fieldName} from`
        );
        invalidValueCount++;
        continue;
      }

      // Special handling for AlarmId fields
      let valueToSet = newValue;
      if (fieldName === "AlarmId" && newValue === "__TAG_IDENTIFIER__") {
        valueToSet = parseInt(tag.TagIdentifier);
      } else if (
        fieldName === "Alarm2Id" &&
        newValue === "5+__TAG_IDENTIFIER__"
      ) {
        valueToSet = parseInt("5" + tag.TagIdentifier.toString());
      } else if (fieldInfo?.type === "bool") {
        valueToSet = coerceToBoolean(newValue);
      }

      const updatedTag = {
        ...tag,
        [fieldName]: valueToSet,
      };

      if (lookupFieldMappings[fieldName]) {
        const mapping = lookupFieldMappings[fieldName];
        const stringValue = lookupIdToName(
          valueToSet,
          mapping.category,
          lookupValues
        );
        if (stringValue) {
          updatedTag[mapping.stringField] = stringValue;
        }
      }

      newEditedTags[index] = updatedTag;
    }

    newDirtyTags.add(index);
    updatedCount++;
  }

  return {
    editedTags: newEditedTags,
    dirtyTags: newDirtyTags,
    updatedCount,
    skippedCount,
    invalidValueCount,
  };
};

/**
 * The snackbar text and severity reporting a bulk update's result.
 * @param {object} result
 * @param {object} result.fieldInfo The bulk update field.
 * @param {number} result.updatedCount
 * @param {number} result.skippedCount Tags skipped for an incompatible source type.
 * @param {number} [result.invalidValueCount] Tags skipped because they could not take the value.
 * @returns {{ message: string, severity: "success" | "warning" }}
 */
export const bulkUpdateSummary = ({
  fieldInfo,
  updatedCount,
  skippedCount,
  invalidValueCount = 0,
}) => {
  const fieldLabel = fieldInfo?.name ?? fieldInfo?.key;
  const skipReasons = [
    skippedCount > 0 && `${skippedCount} skipped — incompatible source type`,
    invalidValueCount > 0 && `${invalidValueCount} skipped — invalid value`,
  ].filter(Boolean);
  const skippedSuffix =
    skipReasons.length > 0 ? ` (${skipReasons.join("; ")})` : "";
  return {
    message: `Updated ${fieldLabel} for ${updatedCount} tag${updatedCount !== 1 ? "s" : ""}${skippedSuffix}`,
    severity: updatedCount > 0 ? "success" : "warning",
  };
};
